import api from "./api";

export type PeriodoEstatisticasPreset = "ytd" | "mes" | "ano_anterior";

export type DashboardEstatisticasResponse = {
  periodo: {
    preset: PeriodoEstatisticasPreset;
    dataInicio: string;
    dataFim: string;
    dataInicioAnterior: string;
    dataFimAnterior: string;
    periodoLabel: string;
  };
  operacional: {
    eventos: { abertos: number; emProcessamento: number; encerrados: number };
    inscricoes: {
      total: number;
      totalAnterior: number;
      variacaoPercentual: number | null;
    };
    funil: { pago: number; pendente: number; cancelado: number };
  };
  receita: {
    ytd: { atual: number; anterior: number; periodoLabel: string };
    mensalYoy: {
      labels: string[];
      anoAtual: number[];
      anoAnterior: number[];
      anoAtualLabel: string;
      anoAnteriorLabel: string;
    };
    porTipoPagamento: Array<{ tipo: string; total: number; qtd: number }>;
    eventosVsLoja: {
      labels: string[];
      eventos: number[];
      loja: number[];
    };
  };
  inscricoes: {
    mensalYoy: {
      labels: string[];
      anoAtual: number[];
      anoAnterior: number[];
      anoAtualLabel: string;
      anoAnteriorLabel: string;
    };
    porDia: { labels: string[]; data: number[] };
    porModalidade: Array<{ modalidade: string; qtd: number }>;
    porCidade: Array<{ cidade: string; qtd: number }>;
    porEquipe: Array<{ equipe: string; qtd: number }>;
    porCanal: {
      site: number;
      app: number;
      appIos: number;
      appAndroid: number;
      appIndeterminado: number;
      painel: number;
    };
  };
  ocupacaoEventos: Array<{
    titulo: string;
    slug: string;
    inscritos: number;
    limite: number;
    percentual: number;
  }>;
  loja: {
    faturamentoLiquido: number;
    faturamentoAnterior: number;
    pedidosPagos: number;
    ticketMedio: number;
  };
  financeiro: {
    receberAberto: number;
    receberVencido: number;
    ticketMedioInscricoes: number;
  };
};

export type OrcamentoEstatisticasResumo = {
  porStatus: Array<{ status: string; label: string; qtd: number }>;
  pipelineValor: number;
  abertas: number;
};

function ensureSuccess<T>(response: { status: number; data: T }): T {
  if (response.status >= 400) {
    const data = response.data as { message?: string };
    throw new Error(data?.message || "Falha ao carregar estatísticas de orçamento");
  }
  return response.data;
}

function normalizeOrcamentoResumo(
  data: Partial<OrcamentoEstatisticasResumo>,
): OrcamentoEstatisticasResumo {
  return {
    abertas: data.abertas ?? 0,
    pipelineValor: data.pipelineValor ?? 0,
    porStatus: Array.isArray(data.porStatus) ? data.porStatus : [],
  };
}

export async function getDashboardEstatisticas(
  periodo: PeriodoEstatisticasPreset = "ytd",
): Promise<DashboardEstatisticasResponse> {
  const { data } = await api.get<DashboardEstatisticasResponse>(
    "/rel/estatisticas/dashboard",
    { params: { periodo } },
  );
  return data;
}

export async function getOrcamentoEstatisticasResumo(): Promise<OrcamentoEstatisticasResumo> {
  const response = await api.get<OrcamentoEstatisticasResumo>(
    "/orcamento/estatisticas/resumo",
  );
  return normalizeOrcamentoResumo(ensureSuccess(response));
}
